/**
 * Maps server product documents → modamai `ProductCard` + PDP shapes.
 */
import { enrichCatalogProduct } from './catalogPdp';

/** @param {string} colorName */
export function getColorHex(colorName) {
  if (!colorName) return '#CCCCCC';
  const colorMap = {
    black: '#000000',
    white: '#FFFFFF',
    red: '#FF0000',
    blue: '#0000FF',
    green: '#008000',
    yellow: '#FFFF00',
    pink: '#FFC0CB',
    purple: '#800080',
    orange: '#FFA500',
    brown: '#A52A2A',
    gray: '#808080',
    grey: '#808080',
    beige: '#F5F5DC',
    navy: '#000080',
    khaki: '#C3B091',
    ivory: '#FFFFF0',
    cream: '#FFFDD0',
    gold: '#FFD700',
    silver: '#C0C0C0',
    noir: '#0d0d0d',
    sand: '#d8c9b1',
    champagne: '#c9a35a',
  };
  const k = String(colorName).toLowerCase().trim();
  return colorMap[k] || '#CCCCCC';
}

export function normalizeColorList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) =>
    typeof c === 'string'
      ? { name: c, hex: getColorHex(c) }
      : {
          name: c?.name != null ? String(c.name) : '',
          hex: c?.hex || getColorHex(c?.name),
        },
  );
}

function firstImageUrl(p) {
  const images = p?.images;
  if (!images?.length) return '';
  const entry = images[0];
  if (typeof entry === 'string') {
    const t = entry.trim();
    if (t.startsWith('http://') || t.startsWith('https://')) return t;
    if (t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed) && parsed[0] && typeof parsed[0] === 'string') return parsed[0];
      } catch {
        /* ignore */
      }
    }
  }
  return '';
}

function secondImageUrl(p) {
  const images = p?.images;
  if (!images || images.length < 2) return undefined;
  return firstImageUrl({ images: [images[1]] }) || undefined;
}

function normalizeImagesArray(images) {
  if (!Array.isArray(images)) return [];
  const out = [];
  const seen = new Set();
  for (const img of images) {
    const u =
      typeof img === 'string'
        ? firstImageUrl({ images: [img] })
        : '';
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

function listCardPrice(p) {
  const variants = Array.isArray(p.variants)
    ? p.variants.filter((v) => v != null && v.price != null && !Number.isNaN(Number(v.price)))
    : [];

  if (variants.length === 0) {
    const displayPrice = Number(p.price) || 0;
    const compareAt =
      p.compareAtPrice != null && p.compareAtPrice !== '' ? Number(p.compareAtPrice) : undefined;
    const hasCompareAt = compareAt != null && !Number.isNaN(compareAt) && compareAt > displayPrice;
    const originalPrice = hasCompareAt ? compareAt : undefined;
    const isSale = Boolean(originalPrice);
    const salePercent =
      isSale && compareAt ? Math.round((1 - displayPrice / compareAt) * 100) : undefined;
    return { displayPrice, originalPrice, isSale, salePercent };
  }

  let best = variants[0];
  let bestPrice = Number(best.price);
  for (let i = 1; i < variants.length; i++) {
    const pr = Number(variants[i].price);
    if (!Number.isNaN(pr) && pr < bestPrice) {
      bestPrice = pr;
      best = variants[i];
    }
  }

  const compareAtVariant =
    best.compareAtPrice != null && best.compareAtPrice !== '' ? Number(best.compareAtPrice) : undefined;
  const compareAtProduct =
    p.compareAtPrice != null && p.compareAtPrice !== '' ? Number(p.compareAtPrice) : undefined;
  const compareAt = compareAtVariant ?? compareAtProduct;
  const hasCompareAt = compareAt != null && !Number.isNaN(compareAt) && compareAt > bestPrice;
  const originalPrice = hasCompareAt ? compareAt : undefined;
  const isSale = Boolean(originalPrice);
  const salePercent =
    isSale && compareAt ? Math.round((1 - bestPrice / compareAt) * 100) : undefined;

  return { displayPrice: bestPrice, originalPrice, isSale, salePercent };
}

function deptFromCategory(cat) {
  if (typeof cat === 'object' && cat?.slug)
    return String(cat.slug).toLowerCase().replace(/^\/+|\/+$/g, '');
  if (typeof cat === 'string') return cat.toLowerCase();
  return '';
}

/**
 * Rough map of API category slug → storefront department slug for `/shop/:category`.
 */
function categoryToDepartment(catField) {
  const sl = deptFromCategory(catField);
  if (!sl) return 'women';
  if (sl.includes('men') || sl === 'menswear') return 'men';
  if (
    ['accessories', 'accessory', 'bags', 'shoes'].some((x) =>
      sl.includes(x),
    )
  )
    return 'accessories';
  return 'women';
}

/** Turn `womens-knitwear` into a short display label when API has slug but no name. */
function humanizeCategorySlug(slug) {
  if (!slug || typeof slug !== 'string') return '';
  const s = slug.replace(/^\/+|\/+$/g, '').trim();
  if (!s) return '';
  return s
    .split(/[-_/]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Card eyebrow line (maison/client `mapProduct`: populated category, else `categoryName`).
 * Important: do not default to `'Collection'` before reading `categoryName` — that hid legacy CJ names.
 */
function resolveCatalogCollectionLabel(p) {
  const cat = p?.category;
  if (typeof cat === 'object' && cat != null) {
    const name = cat.name != null ? String(cat.name).trim() : '';
    if (name) return name;
    const cslug = cat.slug != null ? String(cat.slug).trim() : '';
    if (cslug) return humanizeCategorySlug(cslug) || cslug;
  }
  const legacy = p?.categoryName != null ? String(p.categoryName).trim() : '';
  if (legacy) return legacy;
  const col = p?.collection != null ? String(p.collection).trim() : '';
  if (col) return col;
  const brand = p?.brand != null ? String(p.brand).trim() : '';
  if (brand) return brand;
  return 'Collection';
}

/**
 * Single product doc → PLP/card row (URLs for `image`/`hoverImage`; `id` = Mongo `_id` for cart/wishlist).
 */
export function mapApiProductToModamaiCatalog(p) {
  if (!p) return null;
  const mongoId = p._id != null ? String(p._id) : '';
  const slug =
    typeof p.slug === 'string' && p.slug.trim() ? String(p.slug).trim() : mongoId || '';
  const { displayPrice, originalPrice } = listCardPrice(p);
  const rawColors = p.availableColors || p.colors || [];
  const colors = normalizeColorList(rawColors);
  const sizes = p.allSizes || p.availableSizes || p.sizes || [];
  const created = p.createdAt ? new Date(p.createdAt).getTime() : 0;
  const isRecent = Boolean(created && Date.now() - created < 30 * 24 * 60 * 60 * 1000);

  const tags = [];
  if (
    originalPrice != null &&
    !Number.isNaN(Number(displayPrice)) &&
    Number(displayPrice) < Number(originalPrice)
  ) {
    tags.push('sale');
  }
  if (isRecent) tags.push('new');

  /** PLP `?tag=` filter: server `browseTagIds` (populated) + optional legacy `facets` — not promo `tags`. */
  const facets = [];
  if (Array.isArray(p.browseTagIds)) {
    for (const bt of p.browseTagIds) {
      if (bt && typeof bt === 'object' && bt.slug) {
        facets.push(String(bt.slug).toLowerCase());
      }
    }
  }
  if (Array.isArray(p.facets)) {
    facets.push(
      ...p.facets.map((t) =>
        typeof t === 'object' ? String(t.slug || t.name || '').toLowerCase() : String(t).toLowerCase(),
      ),
    );
  }

  const collectionLabel = resolveCatalogCollectionLabel(p);

  return {
    id: mongoId || slug,
    slug,
    name: p.name || '',
    price: Math.round(Number(displayPrice) * 100) / 100,
    comparePrice:
      originalPrice != null ? Math.round(Number(originalPrice) * 100) / 100 : undefined,
    image: firstImageUrl(p),
    hoverImage: secondImageUrl(p),
    category: categoryToDepartment(p.category),
    collection: collectionLabel,
    tags,
    facets,
    colors,
    sizes,
    images: normalizeImagesArray(p.images),
    description: p.description || '',
  };
}

export function mapApiProductsToModamaiCatalog(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapApiProductToModamaiCatalog).filter(Boolean);
}

/**
 * PDP: includes API `colorSizeMap`/`variants`; runs through enrichCatalogProduct.
 */
export function mapApiProductToModamaiDetail(p) {
  const catalog = mapApiProductToModamaiCatalog(p);
  if (!catalog) return null;
  const mongoId = p._id != null ? String(p._id) : catalog.id;

  let colorSizeMap = {};
  if (p.colorSizeMap && typeof p.colorSizeMap === 'object') {
    colorSizeMap = p.colorSizeMap;
  } else if (Array.isArray(p.variants)) {
    /** Build boolean map where possible */
    p.variants.forEach((v) => {
      if (!v?.name && !v?.variantKey) return;
      const parts = String(v.variantNameEn || v.name || v.variantKey || '').split(/\s+/);
      if (parts.length < 2) return;
      const size = parts[parts.length - 1];
      const color = parts.slice(0, -1).join(' ');
      if (!colorSizeMap[color]) colorSizeMap[color] = {};
      colorSizeMap[color][size] = true;
    });
  }

  const pre = {
    ...catalog,
    id: mongoId,
    mongoId,
    brand: typeof p.brand === 'string' ? p.brand : '',
    description: catalog.description || p.description || '',
    images:
      normalizeImagesArray(p.images).length > 0
        ? normalizeImagesArray(p.images)
        : [catalog.image].filter(Boolean),
    colorSizeMap: Object.keys(colorSizeMap).length ? colorSizeMap : undefined,
    variants: Array.isArray(p.variants) ? p.variants : [],
    variantMap: typeof p.variantMap === 'object' && p.variantMap ? p.variantMap : {},
    isAvailable: p.isAvailable !== false,
    saleEnds:
      typeof p.saleEnds === 'string' && p.saleEnds.trim()
        ? p.saleEnds
        : catalog.tags.includes('sale')
          ? 'Offer ends whilst stock lasts.'
          : undefined,
  };

  return enrichCatalogProduct(pre);
}
